import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Mountain, Users, Calendar, FolderOpen, 
  BookOpen, Images, Bed, MessageSquare, Heart, Settings,
  LogOut, Menu, X, Search, Plus, Edit, Trash2, Upload, Save, Eye, ExternalLink
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { LANGUAGES } from '../../contexts/LanguageContext';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { ImageUploader } from '../ImageUpload';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SIDEBAR_ITEMS = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/admin' },
  { icon: Mountain, label: 'Falesie', path: '/admin/crags' },
  { icon: Users, label: 'Team', path: '/admin/team' },
  { icon: Calendar, label: 'Eventi', path: '/admin/events' },
  { icon: FolderOpen, label: 'Progetti', path: '/admin/projects' },
  { icon: BookOpen, label: 'Blog', path: '/admin/blog' },
  { icon: Images, label: 'Gallery', path: '/admin/gallery' },
  { icon: Bed, label: 'Alloggi', path: '/admin/accommodations' },
  { icon: MessageSquare, label: 'Messaggi', path: '/admin/messages' },
  { icon: Heart, label: 'Donazioni', path: '/admin/donations' },
  { icon: Search, label: 'SEO', path: '/admin/seo' },
  { icon: Upload, label: 'Uploads', path: '/admin/uploads' },
  { icon: Settings, label: 'Impostazioni', path: '/admin/settings' },
];

function AdminSidebar({ currentPath, sidebarOpen, setSidebarOpen, logout }) {
  return (
    <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#0A0A0A] transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
      <div className="flex items-center justify-between h-16 px-6 border-b border-gray-800">
        <Link to="/" className="text-white font-heading text-lg uppercase">CMS Admin</Link>
        <button className="lg:hidden text-white" onClick={() => setSidebarOpen(false)}><X className="w-6 h-6" /></button>
      </div>
      <nav className="p-4 space-y-1 max-h-[calc(100vh-8rem)] overflow-y-auto">
        {SIDEBAR_ITEMS.map((item) => (
          <Link key={item.path} to={item.path} className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${currentPath === item.path ? 'bg-[#FF6D00] text-white' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}>
            <item.icon className="w-5 h-5" /><span className="text-sm">{item.label}</span>
          </Link>
        ))}
      </nav>
      <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-800">
        <button onClick={logout} className="flex items-center gap-3 px-4 py-3 text-gray-400 hover:text-white w-full">
          <LogOut className="w-5 h-5" /><span className="text-sm">Logout</span>
        </button>
      </div>
    </aside>
  );
}

function TranslatedInput({ label, value, onChange, placeholder, multiline = false }) {
  const [activeLang, setActiveLang] = useState('it');
  const Component = multiline ? Textarea : Input;
  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium">{label}</label>
      <Tabs value={activeLang} onValueChange={setActiveLang}>
        <TabsList className="h-8">
          {Object.values(LANGUAGES).map(lang => (<TabsTrigger key={lang.code} value={lang.code} className="text-xs px-2 py-1">{lang.flag}</TabsTrigger>))}
        </TabsList>
        {Object.values(LANGUAGES).map(lang => (
          <TabsContent key={lang.code} value={lang.code} className="mt-2">
            <Component value={value?.[lang.code] || ''} onChange={(e) => onChange({ ...value, [lang.code]: e.target.value })} placeholder={`${placeholder} (${lang.name})`} rows={multiline ? 6 : undefined} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

export function AdminBlog() {
  const { logout, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showDialog, setShowDialog] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { navigate('/admin/login'); return; }
    fetchPosts();
  }, [isAuthenticated, authLoading, navigate]);

  const fetchPosts = async () => {
    try { 
      const res = await axios.get(`${API}/blog?published_only=false`); 
      setPosts(res.data); 
    }
    catch (error) { toast.error('Errore nel caricamento'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    try {
      if (editing.id && posts.find(p => p.id === editing.id)) {
        await axios.put(`${API}/blog/${editing.id}`, editing);
        toast.success('Articolo aggiornato');
      } else {
        await axios.post(`${API}/blog`, editing);
        toast.success('Articolo creato');
      }
      setShowDialog(false); 
      fetchPosts();
    } catch (error) { toast.error('Errore nel salvataggio'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo articolo?')) return;
    try { 
      await axios.delete(`${API}/blog/${id}`); 
      toast.success('Articolo eliminato'); 
      fetchPosts(); 
    }
    catch (error) { toast.error('Errore nell\'eliminazione'); }
  };

  const togglePublish = async (post) => {
    try {
      const updatedPost = {
        ...post,
        is_published: !post.is_published,
        published_at: !post.is_published ? new Date().toISOString() : post.published_at
      };
      await axios.put(`${API}/blog/${post.id}`, updatedPost);
      toast.success(updatedPost.is_published ? 'Articolo pubblicato' : 'Articolo nascosto');
      fetchPosts();
    } catch (error) { toast.error('Errore nel cambio stato'); }
  };

  const openNew = () => {
    setEditing({
      title: { it: '', en: '', fr: '', de: '', es: '' },
      content: { it: '', en: '', fr: '', de: '', es: '' },
      excerpt: { it: '', en: '', fr: '', de: '', es: '' },
      category: '',
      tags: [],
      featured_image: '',
      slug: '',
      is_published: false
    });
    setShowDialog(true);
  };

  const generateSlug = (title) => {
    return (title || '').toLowerCase()
      .replace(/[àáâãäå]/g, 'a')
      .replace(/[èéêë]/g, 'e')
      .replace(/[ìíîï]/g, 'i')
      .replace(/[òóôõö]/g, 'o')
      .replace(/[ùúûü]/g, 'u')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 50);
  };

  if (authLoading || !isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-blog">
      <AdminSidebar currentPath="/admin/blog" sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} logout={logout} />
      <div className="lg:ml-64">
        <header className="bg-white border-b h-16 flex items-center justify-between px-6">
          <button className="lg:hidden" onClick={() => setSidebarOpen(true)}><Menu className="w-6 h-6" /></button>
          <h1 className="font-heading text-xl uppercase">Gestione Blog</h1>
          <Button onClick={openNew} className="btn-primary"><Plus className="w-4 h-4 mr-2" />Nuovo Articolo</Button>
        </header>

        <main className="p-6">
          {loading ? (
            <div className="flex justify-center py-20"><div className="animate-spin w-8 h-8 border-4 border-[#FF6D00] border-t-transparent rounded-full" /></div>
          ) : posts.length === 0 ? (
            <div className="text-center py-20">
              <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">Nessun articolo ancora</p>
              <Button onClick={openNew} className="btn-primary">Crea il primo articolo</Button>
            </div>
          ) : (
            <div className="bg-white rounded-lg border overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left py-4 px-6 font-heading uppercase text-sm">Titolo</th>
                    <th className="text-left py-4 px-6 font-heading uppercase text-sm">Categoria</th>
                    <th className="text-left py-4 px-6 font-heading uppercase text-sm">Data</th>
                    <th className="text-left py-4 px-6 font-heading uppercase text-sm">Stato</th>
                    <th className="text-right py-4 px-6 font-heading uppercase text-sm">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {posts.map((post) => (
                    <tr key={post.id} className="border-b hover:bg-gray-50">
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          {post.featured_image && (
                            <img src={post.featured_image.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${post.featured_image}` : post.featured_image} alt="" className="w-12 h-12 rounded object-cover" />
                          )}
                          <div>
                            <p className="font-medium">{post.title?.it || post.title?.en || '-'}</p>
                            <p className="text-xs text-gray-500">{post.slug}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-gray-500">{post.category || '-'}</td>
                      <td className="py-4 px-6 text-sm text-gray-500">
                        {post.published_at ? new Date(post.published_at).toLocaleDateString('it-IT') : 'Bozza'}
                      </td>
                      <td className="py-4 px-6">
                        <button 
                          onClick={() => togglePublish(post)}
                          className={`px-3 py-1 rounded text-xs font-medium transition-colors ${post.is_published ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                        >
                          {post.is_published ? 'Pubblicato' : 'Bozza'}
                        </button>
                      </td>
                      <td className="py-4 px-6 text-right">
                        <div className="flex justify-end gap-2">
                          <Link to={`/team/blog/${post.slug}`} target="_blank" className="p-2 hover:bg-gray-100 rounded">
                            <Eye className="w-4 h-4 text-gray-500" />
                          </Link>
                          <button onClick={() => { setEditing(post); setShowDialog(true); }} className="p-2 hover:bg-gray-100 rounded">
                            <Edit className="w-4 h-4 text-blue-600" />
                          </button>
                          <button onClick={() => handleDelete(post.id)} className="p-2 hover:bg-gray-100 rounded">
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </main>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.id ? 'Modifica Articolo' : 'Nuovo Articolo'}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4 py-4">
              <TranslatedInput 
                label="Titolo" 
                value={editing.title} 
                onChange={(v) => setEditing({
                  ...editing, 
                  title: v, 
                  slug: editing.slug || generateSlug(v.it || v.en)
                })} 
                placeholder="Titolo articolo" 
              />
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Slug (URL)</label>
                  <Input 
                    value={editing.slug} 
                    onChange={(e) => setEditing({...editing, slug: generateSlug(e.target.value)})} 
                    placeholder="url-articolo"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Categoria</label>
                  <Input 
                    value={editing.category} 
                    onChange={(e) => setEditing({...editing, category: e.target.value})} 
                    placeholder="es. News, Guide, Eventi"
                  />
                </div>
              </div>

              <TranslatedInput 
                label="Estratto (anteprima)" 
                value={editing.excerpt} 
                onChange={(v) => setEditing({...editing, excerpt: v})} 
                placeholder="Breve descrizione per l'anteprima" 
                multiline 
              />
              
              <TranslatedInput 
                label="Contenuto" 
                value={editing.content} 
                onChange={(v) => setEditing({...editing, content: v})} 
                placeholder="Contenuto completo dell'articolo" 
                multiline 
              />
              
              <div>
                <label className="block text-sm font-medium mb-2">Tags (separati da virgola)</label>
                <Input 
                  value={editing.tags?.join(', ') || ''} 
                  onChange={(e) => setEditing({...editing, tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean)})} 
                  placeholder="es. arrampicata, sicilia, eventi"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Immagine in evidenza</label>
                <ImageUploader 
                  value={editing.featured_image ? [editing.featured_image] : []} 
                  onChange={(images) => setEditing({...editing, featured_image: images[0]?.url || images[0] || ''})}
                  category="blog"
                  multiple={false}
                  maxImages={1}
                />
              </div>
              
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  id="is_published"
                  checked={editing.is_published} 
                  onChange={(e) => setEditing({
                    ...editing, 
                    is_published: e.target.checked,
                    published_at: e.target.checked ? new Date().toISOString() : editing.published_at
                  })} 
                />
                <label htmlFor="is_published" className="text-sm">Pubblica immediatamente</label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Annulla</Button>
            <Button onClick={handleSave} className="btn-primary"><Save className="w-4 h-4 mr-2" />Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
